import type React from 'react';

import { ProgressStepper, Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import { SHARE_GOAL_COUNTER_STEPS } from '#src/app/pages/psf/share/shared/consts';

import { ShareGoalCounterEffects } from './components/ShareGoalCounterEffects';
import { useShareGoalCounterDisplay } from './hooks/useShareGoalCounterDisplay';
import { useSupporterName } from './hooks/useSupporterName';

type Props = Readonly<{
	count: number;
}>;

/**
 * Share Goal Counter (CHANGE-97807) `variant_1`: per-state copy and the DS ProgressStepper, with decorative
 * motion drawn over it by `ShareGoalCounterEffects`.
 */
export const ShareGoalCounter: React.FC<Props> = ({ count }) => {
	const { translate } = useI18n();
	const name = useSupporterName();
	const { displayCount, stepperCount, isRevealing, showEffects } = useShareGoalCounterDisplay(count);
	const isComplete = displayCount === SHARE_GOAL_COUNTER_STEPS;

	return (
		<div className="flex flex-col gap-6 text-center" data-qa="share-goal-counter">
			<div
				key={displayCount}
				className="animate-in fade-in flex flex-col gap-3 duration-300 motion-reduce:animate-none"
			>
				<Text
					variant="heading"
					size="large"
					as="h1"
					className="text-typography-strong m-0"
					data-qa="share-goal-counter-headline"
				>
					{translate(`corgi.psf.share.sharing_goal.counter_experiment.counter_${displayCount}.headline`)}
				</Text>
				<Text variant="body" className="text-typography-strong" data-qa="share-goal-counter-body">
					{isComplete && !name
						? translate('corgi.psf.share.sharing_goal.counter_experiment.counter_3.body_no_name')
						: translate(`corgi.psf.share.sharing_goal.counter_experiment.counter_${displayCount}.body`, { name })}
				</Text>
			</div>

			<div className="relative">
				{/* The stepper lags the count on purpose; the status below is what screen readers get. */}
				<div aria-hidden>
					<ProgressStepper currentStepNumber={stepperCount + 1} numberOfSteps={SHARE_GOAL_COUNTER_STEPS} />
				</div>
				{showEffects && <ShareGoalCounterEffects count={displayCount} isRevealing={isRevealing} />}
				<output className="sr-only">
					{translate('corgi.psf.share.sharing_goal.counter_experiment.aria_label', { n: displayCount })}
				</output>
			</div>
		</div>
	);
};
